/* eslint-disable no-unused-vars */
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { history } from "../../../routing/history";
import { Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import TwoFaCode from "../../../components/core/profile-settings/security/TwoFaCode";
import CompanyHeading from "../../../components/theming/CompanyHeading";
import { verify2FaCode } from "../../../redux/auth2fa/verify/actions";
import { resend2FaCode } from "../../../redux/auth2fa/resend-code/actions";
import routes from "../../../routing/routes";
import { AppDispatch } from "../../../redux/auth2fa/resend-code/types";

/**
 * @component Verify2FaCodePage
 * @description
 * A dedicated page that allows users to verify their 2FA code after login.
 * It retrieves the `userId` and redirect path (`from`) from session storage.
 * If the session is invalid or expired, the user is redirected to the login page.
 *
 * Provides handlers to:
 * - Submit the 2FA code for verification
 * - Resend a new 2FA code
 * - Cancel and clear the session
 *
 * Uses the reusable <TwoFaCode /> component to render the form UI.
 */
export const Verify2FaCodePage: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation(["authentication", "common", "snackbars"]);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const userId = sessionStorage.getItem("pending_2fa_user_id");
  const from = JSON.parse(sessionStorage.getItem("2fa_from") || "{}");

  /**
   * @function guardUserSession
   * Executes a given callback if `userId` is available (valid session).
   * Otherwise, clears session storage, shows an error, and redirects to login.
   *
   * @param callback - Function that receives `userId` when available
   */
  const guardUserSession = (callback: (safeUserId: string) => void) => {
    if (!userId) {
      enqueueSnackbar(t("auth2fa.sessionExpired", { ns: "snackbars" }), {
        variant: "error",
      });
      sessionStorage.removeItem("pending_2fa_user_id");
      sessionStorage.removeItem("2fa_from");
      history.push(routes.login);
      return;
    }
    callback(userId);
  };

  /**
   * @function handleSubmit
   * @description Verifies the provided 2FA code using the userId from session storage.
   * @param code - 6-digit 2FA code entered by the user
   */
  const handleSubmit = (code: string) =>
    guardUserSession((safeUserId) =>
      dispatch(verify2FaCode(safeUserId, code, dispatch, from))
    );

  /**
   * @function handleCancel
   * @description Cancels the 2FA process, clears session data, and redirects to login.
   */
  const handleCancel = () => {
    sessionStorage.removeItem("pending_2fa_user_id");
    sessionStorage.removeItem("2fa_from");
    history.push(routes.login);
  };

  /**
   * @function handleResend
   * @description Resends the 2FA code to the user's registered email.
   * If unauthorized (401), clears the session and redirects to login.
   */
  const handleResend = () =>
    guardUserSession((safeUserId) => {
      (dispatch as AppDispatch)(resend2FaCode(safeUserId))
        .then(() => {
          enqueueSnackbar(t("auth2fa.codeResent", { ns: "snackbars" }), {
            variant: "success",
          });
        })
        .catch((error: { response: { status: number } }) => {
          if (error?.response?.status === 401) {
            sessionStorage.removeItem("pending_2fa_user_id");
            sessionStorage.removeItem("2fa_from");
            history.push(routes.login);
          }
        });
    });

  return (
    <Grid
      container
      className={classes.grid}
      direction="column"
      justifyContent="center"
      alignItems="center"
    >
      <Grid item xs={12} sm={8}>
        <CompanyHeading>
          {t("auth2fa.title", {
            ns: "authentication",
          })}
        </CompanyHeading>

        <Typography align="center" className={classes.text}>
          {t("auth2fa.description", {
            ns: "authentication",
          })}
        </Typography>
      </Grid>
      <TwoFaCode
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onResend={handleResend}
      />
    </Grid>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  grid: {
    marginTop: theme.spacing(6),
    alignItems: "flex-start",
    justifyContent: "flex-start",
    textAlign: "start",
  },

  text: {
    marginBottom: theme.spacing(1),
  },
}));
